// 공지 등록·수정 모달(시안 #mNotice). 저장은 이 모달이 하고 결과만 onSaved(저장값, 새것여부)로 올린다.
import { useRef, useState } from "react";
import { createNotice, updateNotice, uploadNoticeImage, errorText } from "./api";
import { EMPTY_FORM, formFrom, validate, buildPayload, insertAt } from "./noticeView";
import { kstFull } from "../../inquiry/view";
import { showToast } from "../../../utill/utill";
import MdPreview from "./MdPreview";
import { useDismiss } from "../../v6common/a11y";

export default function NoticeModal({ notice = null, onClose, onSaved }) {
    const isNew = !notice;
    const [f, setF] = useState(() => (notice ? formFrom(notice) : EMPTY_FORM));
    const [preview, setPreview] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    useDismiss(() => { if (!busy) onClose(); }); // Esc(바깥 클릭은 아래 onMouseDown)
    const ta = useRef(null);
    const caret = useRef(null);
    const fileIn = useRef(null);
    const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    async function pick(e) {
        const files = [...(e.target.files || [])];
        e.target.value = "";
        if (!files.length) return;
        if (files.some((x) => !String(x.type).startsWith("image/"))) { setErr("이미지 파일만 넣을 수 있습니다."); return; }
        setErr(""); setUploading(true);
        try {
            const urls = [];
            for (const file of files) urls.push(await uploadNoticeImage(file));
            setF((o) => {
                const r = insertAt(o.content, caret.current ?? o.content.length, urls);
                caret.current = r.caret;
                return { ...o, content: r.value };
            });
            setPreview(false);
            showToast(`이미지 ${urls.length}장을 내용에 넣었습니다.`);
        } catch (x) {
            setErr(errorText(x));
        } finally {
            setUploading(false);
        }
    }

    async function save() {
        const v = validate(f);
        if (v) { setErr(v); return; }
        setErr(""); setBusy(true);
        try {
            const body = buildPayload(f);
            const saved = isNew ? await createNotice(body) : await updateNotice(notice.id, body);
            onSaved(saved, isNew);
        } catch (x) {
            setErr(errorText(x));
            setBusy(false);
        }
    }

    const remember = () => { if (ta.current) caret.current = ta.current.selectionStart; };

    return (
        <div className="modal on" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
            <div className="mbox" style={{ width: 640 }}>
                <div className="mh"><h3>{isNew ? "공지사항 추가" : "공지사항 수정"}</h3>
                    <button className="btn btn-ghost btn-sm x" aria-label="닫기" onClick={onClose}><i className="fa-solid fa-xmark"></i></button></div>
                <div className="mb">
                    <div className="fld" style={{ marginBottom: 12 }}><label htmlFor="ntTitle">제목 <span className="req">*</span></label>
                        <input className="inp" id="ntTitle" placeholder="공지 제목을 입력하세요" style={{ width: "100%" }} value={f.title} onChange={set("title")} autoFocus /></div>
                    <div className="fld" style={{ marginBottom: 12 }}><label htmlFor="ntBody">내용 (마크다운 지원) <span className="req">*</span></label>
                        <div className="mdbar">
                            <button className="btn btn-sm" type="button" disabled={uploading} onClick={() => { remember(); fileIn.current?.click(); }}>
                                <i className={uploading ? "fa-solid fa-spinner fa-spin" : "fa-regular fa-image"}></i>{uploading ? "올리는 중…" : "이미지 삽입"}</button>
                            <button className="btn btn-sm" type="button" onClick={() => { remember(); setPreview((p) => !p); }}>
                                {preview ? <><i className="fa-solid fa-pen"></i>편집</> : <><i className="fa-regular fa-eye"></i>미리보기</>}</button>
                            <span className="sp"></span><span className="note" style={{ margin: 0 }}>## 제목 · - 목록 · **굵게**</span>
                            <input ref={fileIn} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple hidden onChange={pick} />
                        </div>
                        {preview
                            ? <div className="mdprev"><MdPreview src={f.content} /></div>
                            : <textarea ref={ta} className="ta" id="ntBody" placeholder={"## 제목\n\n- 항목\n- 항목\n\n![](이미지 삽입 버튼 사용)"} style={{ minHeight: 130 }}
                                value={f.content} onChange={set("content")} onBlur={remember} />}
                    </div>
                    <label className="cbx"><input type="checkbox" checked={f.imp} onChange={set("imp")} />
                        <div><b>중요 공지 (키오스크 진입 시 팝업으로 노출)</b><small>체크하지 않으면 일반 공지로 공지사항 메뉴에만 보입니다 · 앱 푸시는 아래 푸시 칸을 체크했을 때만 나갑니다</small></div></label>
                    <label className="cbx"><input type="checkbox" checked={f.push} onChange={set("push")} />
                        <div><b>모바일 앱 푸시 발송</b>
                            <small>게시가 시작되면 가맹점주 앱으로 알림을 보냅니다 (안드로이드만 · 공지당 한 번)
                                {notice?.push_sent_at ? ` · ${kstFull(notice.push_sent_at)} 발송됨` : ""}</small></div></label>
                    <div className="grid2" style={{ margin: "4px 0 12px" }}>
                        <div className="fld"><label htmlFor="ntFrom">게시 시작일</label>
                            <input type="datetime-local" className="inp" id="ntFrom" style={{ width: "100%" }} value={f.start} onChange={set("start")} />
                            <div className="note" style={{ marginTop: 4 }}>비워두면 즉시 게시</div></div>
                        <div className="fld"><label htmlFor="ntTo">게시 종료일</label>
                            <input type="datetime-local" className="inp" id="ntTo" style={{ width: "100%" }} value={f.end} onChange={set("end")} />
                            <div className="note" style={{ marginTop: 4 }}>비워두면 무기한</div></div>
                    </div>
                    <div className="grid2" style={{ marginBottom: 6 }}>
                        <div className="fld"><label htmlFor="ntTitleZh">제목 (中文) <span style={{ color: "var(--tx3)", fontWeight: 400 }}>비우면 한국어 표시</span></label>
                            <input className="inp" id="ntTitleZh" value={f.titleZh} onChange={set("titleZh")} /></div>
                        <div className="fld"><label htmlFor="ntBodyZh">내용 (中文)</label>
                            <input className="inp" id="ntBodyZh" value={f.contentZh} onChange={set("contentZh")} /></div>
                    </div>
                    <div className={`err${err ? " on" : ""}`} role={err ? "alert" : undefined}>{err}</div>
                </div>
                <div className="mf"><button className="btn" onClick={onClose}>취소</button>
                    <button className="btn btn-pri" disabled={busy || uploading} onClick={save}><i className="fa-solid fa-check"></i>{isNew ? "등록" : "저장"}</button></div>
            </div>
        </div>
    );
}
